import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import {
  Users,
  Mail,
  FileText,
  CreditCard,
  CheckCircle2,
  TrendingUp,
  DollarSign,
  Activity,
  ShieldAlert,
  Loader2,
  Eye,
  MousePointerClick,
} from "lucide-react";

const ADMIN_KEY_PARAM = "key";

function Metric({ label, value, sub, icon: Icon, accent = "sky", testId }) {
  const accents = {
    sky: "text-sky-400 border-sky-500/40",
    emerald: "text-emerald-400 border-emerald-500/40",
    amber: "text-amber-400 border-amber-500/40",
    rose: "text-rose-400 border-rose-500/40",
  };
  return (
    <div data-testid={testId} className="bg-[var(--bg-secondary)] p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="tick-label">{label}</div>
        <div className={`w-7 h-7 border ${accents[accent]} flex items-center justify-center`}>
          <Icon className={`w-3.5 h-3.5 ${accents[accent].split(" ")[0]}`} strokeWidth={2} />
        </div>
      </div>
      <div className={`metric-number text-3xl ${accents[accent].split(" ")[0]}`}>{value}</div>
      {sub && <div className="tick-label mt-1.5 text-slate-500">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [params] = useSearchParams();
  const adminKey = params.get(ADMIN_KEY_PARAM) || "";
  const [metrics, setMetrics] = useState(null);
  const [leads, setLeads] = useState([]);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!adminKey) {
      setError("Missing admin key. Append ?key=<ADMIN_KEY> to the URL.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    async function load() {
      try {
        const [m, l, e] = await Promise.all([
          api.get(`/admin/metrics?admin_key=${encodeURIComponent(adminKey)}`),
          api.get(`/admin/leads?admin_key=${encodeURIComponent(adminKey)}`),
          api.get(`/admin/events/recent?admin_key=${encodeURIComponent(adminKey)}&limit=40`),
        ]);
        if (cancelled) return;
        setMetrics(m.data);
        setLeads(l.data);
        setEvents(e.data);
      } catch (err) {
        if (cancelled) return;
        setError(err?.response?.data?.detail || "Unable to load admin metrics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    const t = setInterval(load, 15000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [adminKey]);

  if (loading) {
    return (
      <div className="grain min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="grain min-h-screen">
        <Navbar />
        <div className="max-w-xl mx-auto px-6 py-20">
          <div className="panel-elev p-8">
            <ShieldAlert className="w-8 h-8 text-rose-400 mb-4" />
            <div className="tick-label text-rose-400 mb-2">/ Access Denied</div>
            <h1 className="font-display font-black text-2xl text-slate-100 mb-2">
              Admin authentication required
            </h1>
            <p className="text-slate-400 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const m = metrics || {};

  return (
    <div className="grain min-h-screen">
      <Navbar />
      <div data-testid="admin-dashboard" className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="tick-label text-sky-400 mb-2">/ Customer Validation Dashboard</div>
            <h1 className="font-display font-black text-3xl text-slate-100">Launch Metrics</h1>
            <div className="font-mono text-xs text-slate-500 mt-1">
              Auto-refresh every 15s · Last refresh: {new Date().toLocaleTimeString()}
            </div>
          </div>
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-emerald-400 inline-flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-emerald-400 pulse-dot inline-block" /> Live
          </div>
        </div>

        {/* PRIMARY FUNNEL */}
        <div className="tick-label text-slate-500 mb-3">/ Acquisition Funnel</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[var(--border-soft)] mb-10">
          <Metric label="Visitors" value={m.visitors ?? 0} sub={`${m.page_views ?? 0} page views`} icon={Users} accent="sky" testId="metric-visitors" />
          <Metric label="Leads" value={m.leads ?? 0} sub="Free Assessment requests" icon={Mail} accent="sky" testId="metric-leads" />
          <Metric label="Reports Generated" value={m.reports_generated ?? 0} sub={`${m.reports_total ?? 0} total`} icon={FileText} accent="sky" testId="metric-reports" />
          <Metric label="Checkout Starts" value={m.checkout_starts ?? 0} sub="Stripe sessions" icon={CreditCard} accent="amber" testId="metric-checkout-starts" />
          <Metric label="Purchases" value={m.purchases ?? 0} sub="Completed payments" icon={CheckCircle2} accent="emerald" testId="metric-purchases" />
          <Metric label="Revenue" value={`$${(m.revenue_usd ?? 0).toLocaleString()}`} sub="Lifetime, USD" icon={DollarSign} accent="emerald" testId="metric-revenue" />
        </div>

        {/* RATIOS */}
        <div className="tick-label text-slate-500 mb-3">/ Conversion Ratios</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border-soft)] mb-10">
          <Metric label="Visitor → Purchase" value={`${m.conversion_rate_pct ?? 0}%`} sub="Overall conversion" icon={TrendingUp} accent="emerald" testId="metric-conv" />
          <Metric label="Lead → Purchase" value={`${m.lead_to_purchase_rate_pct ?? 0}%`} sub="Lead nurture quality" icon={Activity} accent="sky" testId="metric-lead-conv" />
          <Metric label="Checkout → Purchase" value={`${m.checkout_to_purchase_rate_pct ?? 0}%`} sub="Stripe completion rate" icon={MousePointerClick} accent="amber" testId="metric-checkout-conv" />
        </div>

        {/* CTA CLICKS / PRICING */}
        <div className="tick-label text-slate-500 mb-3">/ Engagement</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--border-soft)] mb-10">
          <Metric label="CTA Clicks" value={m.cta_clicks ?? 0} sub="All tracked CTAs" icon={MousePointerClick} accent="sky" testId="metric-cta-clicks" />
          <Metric label="Pricing Page Visits" value={m.pricing_views ?? 0} sub="Distinct page views" icon={Eye} accent="sky" testId="metric-pricing-views" />
        </div>

        {/* LEADS LIST */}
        <div className="grid lg:grid-cols-2 gap-px bg-[var(--border-soft)]">
          <div className="bg-[var(--bg-secondary)] p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="tick-label text-sky-400">/ Recent Leads</div>
              <div className="font-mono text-[10px] text-slate-500">{leads.length} captured</div>
            </div>
            {leads.length === 0 ? (
              <div className="text-slate-500 text-sm italic py-8 text-center">No leads yet.</div>
            ) : (
              <div className="space-y-px bg-[var(--border-soft)] max-h-[420px] overflow-y-auto">
                {leads.map((l) => (
                  <div key={l.id} className="bg-[var(--bg-secondary)] p-3" data-testid={`lead-row-${l.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-mono text-sm text-slate-100">{l.email}</div>
                      <div className="font-mono text-[9px] text-slate-500">{(l.created_at || "").slice(0, 19).replace("T", " ")}</div>
                    </div>
                    <div className="font-mono text-[10px] text-slate-400">
                      {l.company_name ? <span className="text-slate-300">{l.company_name}</span> : <span className="text-slate-600">—</span>}
                      {" · "}
                      <span className="text-sky-400">{l.source || "n/a"}</span>
                      {" · "}
                      <span className="text-amber-400">{l.intent || "n/a"}</span>
                      {l.touch_count > 1 && <span className="text-slate-500"> · {l.touch_count} touches</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-[var(--bg-secondary)] p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="tick-label text-sky-400">/ Recent Events</div>
              <div className="font-mono text-[10px] text-slate-500">{events.length} shown</div>
            </div>
            {events.length === 0 ? (
              <div className="text-slate-500 text-sm italic py-8 text-center">No events yet.</div>
            ) : (
              <div className="space-y-px bg-[var(--border-soft)] max-h-[420px] overflow-y-auto">
                {events.map((e, i) => (
                  <div key={i} className="bg-[var(--bg-secondary)] p-2.5 grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-3 font-mono text-[10px] uppercase tracking-widest text-sky-400 truncate">{e.event_type}</div>
                    <div className="col-span-5 font-mono text-[11px] text-slate-300 truncate">
                      {e.path || e.cta_id || e.tier || "—"}
                    </div>
                    <div className="col-span-4 font-mono text-[9px] text-slate-500 text-right truncate">
                      {(e.created_at || "").slice(11, 19)} · {(e.session_id || "").slice(0, 8)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <p className="text-[10px] tracking-widest uppercase text-slate-600 font-mono mt-8">
          Access this dashboard at /dashboard?key=&lt;ADMIN_KEY&gt;. Keep the key private.
        </p>
      </div>
    </div>
  );
}
